import {
    chartRefreshScheduler,
    computeStaleState,
    formatAge,
    FrameBudgetTracker,
    measureFrame,
    OHLCAggregationCache,
    RingBuffer,
    getStaleThresholdMs,
    setSnapshot,
    getSnapshot,
    reportChartRenderMetric,
} from '../../charts/engine';
  import type { CandlePoint } from '../../charts/engine';
import {
  renderAreaChart,
  renderCandlestickChart,
  renderLineChart,
  renderSparklineChart,
} from '../../charts/primitives';
import { fetchBreadth, fetchIndices } from '../../services/market-data';
import { isFeatureEnabled } from '../../services/runtime-config';

const teardownMap = new WeakMap<HTMLElement, () => void>();

type DashboardPersistedSnapshot = {
  candles: CandlePoint[];
  adRatios: number[];
  volatilitySeries: number[];
  breadthMomentumSeries: number[];
  candleRangeSeries: number[];
  dispersionSeries: number[];
  breadthVolSpreadSeries: number[];
  breadthAccelerationSeries: number[];
  momentumVolCouplingSeries: number[];
  returnSkewSeries: number[];
  breadthRecoverySeries: number[];
  indexSeries: Record<string, number[]>;
  lastUpdatedAt: number;
};

export function renderPhase2DashboardPulse(container: HTMLElement): void {
    if (!isFeatureEnabled('charts_v2_engine')) {
        container.innerHTML = '<div class="wm-empty">charts_v2_engine flag is disabled.</div>';
        return;
    }

    teardownMap.get(container)?.();

    container.innerHTML = `
      <div class="v2-chart-card glass-card">
        <div class="card-header">
          <h3>Session Radar</h3>
          <span class="text-xs text-muted">Tier A + Tier C</span>
        </div>

        <div class="v2-chart-grid">
          <div class="v2-chart-slot">
            <div class="v2-chart-title">NIFTY Candlestick (1m cache)</div>
            <canvas id="v2-dash-candle" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">A/D Ratio Area</div>
            <canvas id="v2-dash-area" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot v2-chart-slot--wide">
            <div class="v2-chart-title">Top Index Sparkline Strip</div>
            <div id="v2-dash-spark-strip" class="v2-spark-strip"></div>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Index Volatility Pulse</div>
            <canvas id="v2-dash-volatility" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Pulse Regime Board</div>
            <div id="v2-dash-regime-board" class="v2-sector-board"></div>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Breadth Momentum</div>
            <canvas id="v2-dash-breadth-momentum" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Candle Range Pulse</div>
            <canvas id="v2-dash-candle-range" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Index Dispersion Pulse</div>
            <canvas id="v2-dash-dispersion" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Breadth-Volatility Spread</div>
            <div id="v2-dash-breadth-vol-spread" class="v2-sector-board"></div>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Breadth Acceleration</div>
            <canvas id="v2-dash-breadth-accel" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Momentum-Vol Coupling Pulse</div>
            <canvas id="v2-dash-momentum-vol-coupling" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Index Return Skew Pulse</div>
            <canvas id="v2-dash-return-skew" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Breadth Recovery Pulse</div>
            <canvas id="v2-dash-breadth-recovery" class="v2-chart-canvas"></canvas>
          </div>

          <div class="v2-chart-slot">
            <div class="v2-chart-title">Breadth Shock Meter</div>
            <div id="v2-dash-shock-meter" class="v2-sector-board"></div>
          </div>
        </div>

        <div class="v2-chart-meta">
          <span id="v2-dash-freshness" class="text-xs">Waiting for live feed...</span>
          <span id="v2-dash-frame" class="text-xs text-muted" style="margin-left: var(--space-3);">Frame: --</span>
        </div>

        <div id="v2-dash-stale" class="v2-stale-overlay" style="display:none;">
          Live pulse delayed. Rendering last cached frame.
        </div>
      </div>
    `;

    const candleCanvas = container.querySelector('#v2-dash-candle') as HTMLCanvasElement | null;
    const areaCanvas = container.querySelector('#v2-dash-area') as HTMLCanvasElement | null;
    const sparkStrip = container.querySelector('#v2-dash-spark-strip') as HTMLElement | null;
    const volatilityCanvas = container.querySelector('#v2-dash-volatility') as HTMLCanvasElement | null;
    const regimeBoard = container.querySelector('#v2-dash-regime-board') as HTMLElement | null;
    const breadthMomentumCanvas = container.querySelector('#v2-dash-breadth-momentum') as HTMLCanvasElement | null;
    const candleRangeCanvas = container.querySelector('#v2-dash-candle-range') as HTMLCanvasElement | null;
    const dispersionCanvas = container.querySelector('#v2-dash-dispersion') as HTMLCanvasElement | null;
    const breadthVolSpreadBoard = container.querySelector('#v2-dash-breadth-vol-spread') as HTMLElement | null;
    const breadthAccelCanvas = container.querySelector('#v2-dash-breadth-accel') as HTMLCanvasElement | null;
    const momentumVolCouplingCanvas = container.querySelector('#v2-dash-momentum-vol-coupling') as HTMLCanvasElement | null;
    const returnSkewCanvas = container.querySelector('#v2-dash-return-skew') as HTMLCanvasElement | null;
    const breadthRecoveryCanvas = container.querySelector('#v2-dash-breadth-recovery') as HTMLCanvasElement | null;
    const breadthShockMeter = container.querySelector('#v2-dash-shock-meter') as HTMLElement | null;
    const freshnessNode = container.querySelector('#v2-dash-freshness') as HTMLElement | null;
    const frameNode = container.querySelector('#v2-dash-frame') as HTMLElement | null;
    const staleNode = container.querySelector('#v2-dash-stale') as HTMLElement | null;

    if (!candleCanvas || !areaCanvas || !sparkStrip || !volatilityCanvas || !regimeBoard || !breadthMomentumCanvas || !candleRangeCanvas || !dispersionCanvas || !breadthVolSpreadBoard || !breadthAccelCanvas || !momentumVolCouplingCanvas || !returnSkewCanvas || !breadthRecoveryCanvas || !breadthShockMeter || !freshnessNode || !frameNode || !staleNode) return;

    const adRatioSeries = new RingBuffer<number>(180);
    const volatilitySeries = new RingBuffer<number>(180);
    const breadthMomentumSeries = new RingBuffer<number>(180);
    const candleRangeSeries = new RingBuffer<number>(180);
    const dispersionSeries = new RingBuffer<number>(180);
    const breadthVolSpreadSeries = new RingBuffer<number>(180);
    const breadthAccelerationSeries = new RingBuffer<number>(180);
    const momentumVolCouplingSeries = new RingBuffer<number>(180);
    const returnSkewSeries = new RingBuffer<number>(180);
    const breadthRecoverySeries = new RingBuffer<number>(180);
    const indexSeries = new Map<string, RingBuffer<number>>();
    const candleCache = new OHLCAggregationCache(60000, 720);
    const frameTracker = new FrameBudgetTracker(200, 16);
    const staleThresholdMs = getStaleThresholdMs('A');
    const snapshotKey = 'phase2:dashboard-pulse';

    let lastUpdatedAt: number | null = null;

    const getLast = (values: number[]): number => values.length > 0 ? values[values.length - 1] : 0;

    const ensureSeries = (symbol: string): RingBuffer<number> => {
        const existing = indexSeries.get(symbol);
        if (existing) return existing;
        const created = new RingBuffer<number>(120);
        indexSeries.set(symbol, created);
        return created;
    };

    const computeCrossIndexVolatility = (): number => {
      const returns: number[] = [];
      indexSeries.forEach((series) => {
        const values = series.toArray();
        if (values.length < 2) return;
        const prev = values[values.length - 2];
        const current = values[values.length - 1];
        if (!Number.isFinite(prev) || prev === 0) return;
        returns.push(((current - prev) / prev) * 100);
      });

      if (returns.length <= 1) return 0;
      const mean = returns.reduce((sum, value) => sum + value, 0) / returns.length;
      const variance = returns.reduce((sum, value) => {
        const delta = value - mean;
        return sum + (delta * delta);
      }, 0) / returns.length;
      return Math.sqrt(variance);
    };

    const computeIndexDivergence = (): number => {
      const returns: number[] = [];
      indexSeries.forEach((series) => {
        const values = series.toArray();
        if (values.length < 2) return;
        const prev = values[values.length - 2];
        const current = values[values.length - 1];
        if (!Number.isFinite(prev) || prev === 0) return;
        returns.push(((current - prev) / prev) * 100);
      });

      if (returns.length <= 1) return 0;
      const maxReturn = Math.max(...returns);
      const minReturn = Math.min(...returns);
      return Number((maxReturn - minReturn).toFixed(2));
    };

    const computeIndexReturnSkew = (): number => {
      const returns: number[] = [];
      indexSeries.forEach((series) => {
        const values = series.toArray();
        if (values.length < 2) return;
        const prev = values[values.length - 2];
        const current = values[values.length - 1];
        if (!Number.isFinite(prev) || prev === 0) return;
        returns.push(((current - prev) / prev) * 100);
      });

      if (returns.length < 3) return 0;
      const mean = returns.reduce((sum, value) => sum + value, 0) / returns.length;
      const variance = returns.reduce((sum, value) => {
        const delta = value - mean;
        return sum + (delta * delta);
      }, 0) / returns.length;
      const stdDev = Math.sqrt(variance);
      if (!Number.isFinite(stdDev) || stdDev <= 1e-9) return 0;
      const skew = returns.reduce((sum, value) => {
        const z = (value - mean) / stdDev;
        return sum + (z * z * z);
      }, 0) / returns.length;
      return Number(skew.toFixed(3));
    };

    const renderRegimeBoard = (): void => {
      const latestAd = getLast(adRatioSeries.toArray());
      const latestVol = getLast(volatilitySeries.toArray());
      const latestBreadthMomentum = getLast(breadthMomentumSeries.toArray());
      const latestRangePulse = getLast(candleRangeSeries.toArray());
      const breadthBias = latestAd >= 1.3 ? 'Risk-On' : latestAd <= 0.8 ? 'Risk-Off' : 'Balanced';
      const volState = latestVol >= 0.9 ? 'High' : latestVol >= 0.45 ? 'Medium' : 'Low';
      const biasTone = breadthBias === 'Risk-On' ? 'terminal-text--up' : breadthBias === 'Risk-Off' ? 'terminal-text--down' : '';
      const volTone = volState === 'High' ? 'terminal-text--down' : volState === 'Low' ? 'terminal-text--up' : '';
      const pulseScore = Math.max(-100, Math.min(100, ((latestAd - 1) * 40) - (latestVol * 30)));
      const pulseTone = pulseScore >= 10 ? 'terminal-text--up' : pulseScore <= -10 ? 'terminal-text--down' : '';
      const divergence = computeIndexDivergence();
      const divergenceTone = divergence >= 1.8 ? 'terminal-text--down' : divergence <= 0.7 ? 'terminal-text--up' : '';
      const breadthMomentumTone = latestBreadthMomentum >= 0.08 ? 'terminal-text--up' : latestBreadthMomentum <= -0.08 ? 'terminal-text--down' : '';
      const rangePulseTone = latestRangePulse >= 1.2 ? 'terminal-text--down' : latestRangePulse <= 0.5 ? 'terminal-text--up' : '';

      regimeBoard.innerHTML = `
        <div class="terminal-list__row"><span>Pulse Bias</span><strong class="${biasTone}">${breadthBias}</strong></div>
        <div class="terminal-list__row"><span>Volatility Regime</span><strong class="${volTone}">${volState} (${latestVol.toFixed(2)})</strong></div>
        <div class="terminal-list__row"><span>Index Divergence</span><strong class="${divergenceTone}">${divergence.toFixed(2)}%</strong></div>
        <div class="terminal-list__row"><span>Breadth Momentum</span><strong class="${breadthMomentumTone}">${latestBreadthMomentum >= 0 ? '+' : ''}${latestBreadthMomentum.toFixed(3)}</strong></div>
        <div class="terminal-list__row"><span>Candle Range Pulse</span><strong class="${rangePulseTone}">${latestRangePulse.toFixed(2)}%</strong></div>
        <div class="terminal-list__row"><span>A/D Ratio</span><strong>${latestAd.toFixed(2)}</strong></div>
        <div class="terminal-list__row"><span>Pulse Score</span><strong class="${pulseTone}">${pulseScore >= 0 ? '+' : ''}${pulseScore.toFixed(1)}</strong></div>
      `;
    };

    const computeBreadthVolSpread = (): number => {
      const latestAd = getLast(adRatioSeries.toArray());
      const latestVol = getLast(volatilitySeries.toArray());
      const breadthEdge = latestAd - 1;
      return Number((breadthEdge - latestVol).toFixed(3));
    };

    const computeMomentumVolCoupling = (): number => {
      const latestMomentum = getLast(breadthMomentumSeries.toArray());
      const latestVol = getLast(volatilitySeries.toArray());
      const denominator = Math.max(0.05, latestVol);
      return Number((latestMomentum / denominator).toFixed(3));
    };

    const computeBreadthRecovery = (): number => {
      const values = adRatioSeries.toArray();
      if (values.length === 0) return 0;
      const tail = values.slice(-12);
      const latest = tail[tail.length - 1];
      const minTail = Math.min(...tail);
      return Number((latest - minTail).toFixed(3));
    };

    const renderBreadthVolSpreadBoard = (): void => {
      const latestSpread = getLast(breadthVolSpreadSeries.toArray());
      const latestDispersion = getLast(dispersionSeries.toArray());
      const spreadTone = latestSpread >= 0.2 ? 'terminal-text--up' : latestSpread <= -0.2 ? 'terminal-text--down' : '';
      const dispersionTone = latestDispersion >= 1.8 ? 'terminal-text--down' : latestDispersion <= 0.7 ? 'terminal-text--up' : '';
      const regime = latestSpread >= 0.2
        ? 'Breadth-Led'
        : latestSpread <= -0.2
          ? 'Vol-Led'
          : 'Balanced';

      breadthVolSpreadBoard.innerHTML = `
        <div class="terminal-list__row"><span>Spread Score</span><strong class="${spreadTone}">${latestSpread >= 0 ? '+' : ''}${latestSpread.toFixed(3)}</strong></div>
        <div class="terminal-list__row"><span>Dispersion Pulse</span><strong class="${dispersionTone}">${latestDispersion.toFixed(2)}%</strong></div>
        <div class="terminal-list__row"><span>Regime</span><strong>${regime}</strong></div>
      `;
    };

    const renderShockMeter = (): void => {
      const latestMomentum = getLast(breadthMomentumSeries.toArray());
      const latestVol = getLast(volatilitySeries.toArray());
      const latestDispersion = getLast(dispersionSeries.toArray());
      const shock = Math.abs(latestMomentum * 100) + (latestVol * 35) + (latestDispersion * 8);
      const capped = Math.max(0, Math.min(100, shock));
      const level = capped >= 70 ? 'High Shock' : capped >= 40 ? 'Elevated' : 'Calm';
      const tone = capped >= 70 ? 'terminal-text--down' : capped >= 40 ? '' : 'terminal-text--up';

      breadthShockMeter.innerHTML = `
        <div class="terminal-list__row"><span>Shock Score</span><strong class="${tone}">${capped.toFixed(1)}</strong></div>
        <div class="terminal-list__row"><span>Regime</span><strong class="${tone}">${level}</strong></div>
      `;
    };

    const draw = () => {
        const sample = measureFrame('dashboard-pulse', () => {
            renderCandlestickChart(candleCanvas, candleCache.getCandles(true));
            renderAreaChart(areaCanvas, adRatioSeries.toArray(), '#15b57a', 'rgba(21,181,122,0.16)');
            renderLineChart(volatilityCanvas, volatilitySeries.toArray(), '#ff9f43');
            renderLineChart(breadthMomentumCanvas, breadthMomentumSeries.toArray(), '#7cc4fa');
            renderLineChart(candleRangeCanvas, candleRangeSeries.toArray(), '#d94736');
            renderLineChart(dispersionCanvas, dispersionSeries.toArray(), '#c67bff');
            renderLineChart(breadthAccelCanvas, breadthAccelerationSeries.toArray(), '#15b57a');
            renderLineChart(momentumVolCouplingCanvas, momentumVolCouplingSeries.toArray(), '#ff9f43');
            renderLineChart(returnSkewCanvas, returnSkewSeries.toArray(), '#7cc4fa');
            renderLineChart(breadthRecoveryCanvas, breadthRecoverySeries.toArray(), '#5aa6ff');

            const sparkEntries = Array.from(indexSeries.entries()).slice(0, 6);
            sparkStrip.innerHTML = sparkEntries.map(([symbol], idx) => {
                const id = `v2-spark-${idx}`;
                return `<div class="v2-spark-item"><span>${symbol}</span><canvas id="${id}" class="v2-spark-canvas"></canvas></div>`;
            }).join('');

            sparkEntries.forEach(([_, series], idx) => {
                const canvas = sparkStrip.querySelector(`#v2-spark-${idx}`) as HTMLCanvasElement | null;
                if (!canvas) return;
                renderSparklineChart(canvas, series.toArray(), '#5aa6ff');
            });

            renderRegimeBoard();
            renderBreadthVolSpreadBoard();
            renderShockMeter();
        });

        frameTracker.push(sample.label, sample.durationMs);
        const avg = frameTracker.getAverageMs();
        const within = frameTracker.isWithinBudget(sample.durationMs);
        frameNode.textContent = `Frame: ${sample.durationMs.toFixed(2)}ms | avg ${avg.toFixed(2)}ms | budget ${within ? 'OK' : 'OVER'}`;
        reportChartRenderMetric({
          id: 'phase2-dashboard-pulse',
          label: 'Dashboard Pulse',
          latestMs: sample.durationMs,
          averageMs: avg,
          budgetMs: frameTracker.getBudgetMs(),
          withinBudget: within,
        });
    };

      const persistState = () => {
        const serializedIndexSeries: Record<string, number[]> = {};
        indexSeries.forEach((series, symbol) => {
          serializedIndexSeries[symbol] = series.toArray();
        });

        setSnapshot<DashboardPersistedSnapshot>(snapshotKey, {
          candles: candleCache.getCandles(true),
          adRatios: adRatioSeries.toArray(),
          volatilitySeries: volatilitySeries.toArray(),
          breadthMomentumSeries: breadthMomentumSeries.toArray(),
          candleRangeSeries: candleRangeSeries.toArray(),
          dispersionSeries: dispersionSeries.toArray(),
          breadthVolSpreadSeries: breadthVolSpreadSeries.toArray(),
          breadthAccelerationSeries: breadthAccelerationSeries.toArray(),
          momentumVolCouplingSeries: momentumVolCouplingSeries.toArray(),
          returnSkewSeries: returnSkewSeries.toArray(),
          breadthRecoverySeries: breadthRecoverySeries.toArray(),
          indexSeries: serializedIndexSeries,
          lastUpdatedAt: lastUpdatedAt || Date.now(),
        });
      };

    const updateTierA = async () => {
        const indices = await fetchIndices();
        if (indices.length === 0) return;

        const primary = indices[0];
        candleCache.addOHLC({
            t: Date.now(),
            o: primary.open,
            h: primary.high,
            l: primary.low,
            c: primary.last,
        });

        const rangePct = primary.open > 0
          ? ((primary.high - primary.low) / primary.open) * 100
          : 0;
        candleRangeSeries.push(rangePct);

        indices.slice(0, 6).forEach((index) => {
            ensureSeries(index.symbol).push(index.last);
        });

        volatilitySeries.push(computeCrossIndexVolatility());
          dispersionSeries.push(computeIndexDivergence());
          breadthVolSpreadSeries.push(computeBreadthVolSpread());
          momentumVolCouplingSeries.push(computeMomentumVolCoupling());
          returnSkewSeries.push(computeIndexReturnSkew());

        lastUpdatedAt = Date.now();
        persistState();
        draw();
    };

    const updateTierC = async () => {
        const breadth = await fetchBreadth();
        if (!breadth) return;

      const prevAd = getLast(adRatioSeries.toArray());
      const prevMomentum = getLast(breadthMomentumSeries.toArray());
      adRatioSeries.push(breadth.adRatio);
      const momentum = breadth.adRatio - prevAd;
      breadthMomentumSeries.push(momentum);
      breadthAccelerationSeries.push(momentum - prevMomentum);
      breadthVolSpreadSeries.push(computeBreadthVolSpread());
      momentumVolCouplingSeries.push(computeMomentumVolCoupling());
      breadthRecoverySeries.push(computeBreadthRecovery());
        lastUpdatedAt = Date.now();
        persistState();
        draw();
    };

    const updateUiFreshness = () => {
        const stale = computeStaleState(lastUpdatedAt, staleThresholdMs);
        staleNode.style.display = stale.stale ? 'flex' : 'none';
        freshnessNode.textContent = stale.stale
            ? `Stale: ${formatAge(stale.ageMs)} old`
            : `Live update ${new Date(lastUpdatedAt || Date.now()).toLocaleTimeString('en-IN')}`;
    };

    const taskAId = `phase2-dash-a-${Math.random().toString(36).slice(2)}`;
    const taskCId = `phase2-dash-c-${Math.random().toString(36).slice(2)}`;
    const uiId = `phase2-dash-ui-${Math.random().toString(36).slice(2)}`;

    const unregisterA = chartRefreshScheduler.register(taskAId, 'A', async () => {
        await updateTierA();
        updateUiFreshness();
    }, 1000);

    const unregisterC = chartRefreshScheduler.register(taskCId, 'C', async () => {
        await updateTierC();
        updateUiFreshness();
    }, 15000);

    const unregisterUi = chartRefreshScheduler.register(uiId, 'B', () => {
        updateUiFreshness();
    }, 3000);

    const persisted = getSnapshot<DashboardPersistedSnapshot>(snapshotKey);
    if (persisted) {
      persisted.candles.forEach(candle => {
        candleCache.addOHLC({
          t: candle.ts,
          o: candle.open,
          h: candle.high,
          l: candle.low,
          c: candle.close,
          volume: candle.volume,
        });
      });

      persisted.adRatios.forEach(value => adRatioSeries.push(value));
      persisted.volatilitySeries?.forEach(value => volatilitySeries.push(value));
      persisted.breadthMomentumSeries?.forEach(value => breadthMomentumSeries.push(value));
      persisted.candleRangeSeries?.forEach(value => candleRangeSeries.push(value));
      persisted.dispersionSeries?.forEach(value => dispersionSeries.push(value));
      persisted.breadthVolSpreadSeries?.forEach(value => breadthVolSpreadSeries.push(value));
      persisted.breadthAccelerationSeries?.forEach(value => breadthAccelerationSeries.push(value));
      persisted.momentumVolCouplingSeries?.forEach(value => momentumVolCouplingSeries.push(value));
      persisted.returnSkewSeries?.forEach(value => returnSkewSeries.push(value));
      persisted.breadthRecoverySeries?.forEach(value => breadthRecoverySeries.push(value));
      Object.entries(persisted.indexSeries).forEach(([symbol, values]) => {
        const series = ensureSeries(symbol);
        values.forEach(value => series.push(value));
      });
      lastUpdatedAt = persisted.lastUpdatedAt;
      draw();
      updateUiFreshness();
    }

    teardownMap.set(container, () => {
        unregisterA();
        unregisterC();
        unregisterUi();
    });
}
